/** Pure campaign status helpers for Maintenance rows (unit-tested). */

function fmtDate(v) {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

export function invoiceStatusNorm(item) {
  return String(item?.invoice_status || '').trim().toLowerCase();
}

export function isTerminalInvoiceStatus(item) {
  return ['paid', 'cancelled', 'canceled'].includes(invoiceStatusNorm(item));
}

/** True when the row has a linked Bexio invoice reference. */
export function hasLinkedBexioInvoice(item) {
  return !!(
    String(item?.linked_invoice_id || '').trim()
    || String(item?.linked_invoice_number || '').trim()
    || String(item?.bexio_invoice_id || '').trim()
  );
}

/**
 * Pre-existing Issued/Invoiced linked invoice — Launch enrols reminders only
 * (no create / PDF / invoice email). Excludes in-progress Launch creates.
 */
export function isExistingIssuedInvoiceForReminders(item) {
  if (!item || item.campaign_launched_at) return false;
  if (!hasLinkedBexioInvoice(item)) return false;
  const st = invoiceStatusNorm(item);
  if (st !== 'issued' && st !== 'invoiced') return false;
  const launchSt = String(item.campaign_launch_status || '').trim().toLowerCase();
  if (launchSt === 'pending' || launchSt === 'invoice_created') return false;
  return true;
}

export function canLaunchCampaign(item) {
  if (!item) return false;
  if (item.campaign_launched_at) return false;
  if (isTerminalInvoiceStatus(item)) return false;
  return true;
}

export function launchCtaLabel(item, { launching = false, compact = false } = {}) {
  if (launching) return compact ? '…' : 'Launching…';
  if (item?.campaign_launched_at) {
    if (isTerminalInvoiceStatus(item)) return 'Stopped (paid/cancelled)';
    return 'Prepared';
  }
  if (isTerminalInvoiceStatus(item)) return 'Stopped (paid/cancelled)';
  if (isExistingIssuedInvoiceForReminders(item)) {
    return compact ? 'Launch reminders' : 'Launch reminders for existing invoice';
  }
  return 'Launch invoice & reminders';
}

export function campaignStatusSummary(item) {
  if (!item) return { launchedLabel: 'Not launched', nextLabel: '—', stopped: false };
  const stopped = !!(item.campaign_stopped || isTerminalInvoiceStatus(item));

  let launchedLabel = 'Not launched';
  if (item.campaign_status_label) {
    launchedLabel = item.campaign_status_label;
  } else if (item.campaign_launched_at) {
    if (stopped) {
      launchedLabel = 'Stopped (paid/cancelled)';
    } else if (item.invoice_email_sent_at) {
      launchedLabel = 'Invoice sent';
    } else if (item.next_fee_date) {
      launchedLabel = `Prepared — invoice sends on ${fmtDate(item.next_fee_date)}`;
    } else {
      launchedLabel = `Prepared ${fmtDate(item.campaign_launched_at)}`;
    }
  } else if (stopped) {
    launchedLabel = 'Stopped (paid/cancelled)';
  } else if (item.campaign_launch_status === 'invoice_created') {
    launchedLabel = 'Partial: invoice created — retry Launch';
  } else if (item.campaign_launch_status === 'failed') {
    launchedLabel = 'Launch failed — retry';
  } else if (isExistingIssuedInvoiceForReminders(item)) {
    launchedLabel = 'Ready — launch reminders for existing invoice';
  }

  const nextLabel = stopped && (item.campaign_launched_at || isTerminalInvoiceStatus(item))
    ? 'Stopped (paid/cancelled)'
    : (item.next_drip_step_label || (item.campaign_launched_at ? 'No future steps' : '—'));
  return { launchedLabel, nextLabel, stopped };
}

export function buildLaunchConfirmMessage(item) {
  const recipients = String(item?.responsible_email || '').trim();
  const fee = Number(item?.maintenance_fee_amount || 0).toFixed(2);
  const currency = item?.currency || 'CHF';
  const domain = item?.domain_name || 'this item';
  const feeDate = item?.next_fee_date ? fmtDate(item.next_fee_date) : 'the next fee date';
  const hasLinked = hasLinkedBexioInvoice(item);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const nfd = item?.next_fee_date ? new Date(item.next_fee_date) : null;
  if (nfd) nfd.setHours(0, 0, 0, 0);
  const early = nfd && nfd.getTime() > today.getTime();

  if (isExistingIssuedInvoiceForReminders(item)) {
    return (
      `Launch reminders for existing invoice on ${domain}?\n\n`
      + 'This will:\n'
      + '• Reuse the already linked Bexio invoice (no new create, PDF download, or invoice email)\n'
      + `• Enrol future reminder steps only for: ${recipients}\n`
      + '• Skip any calendar steps already past at Launch (no backfill)\n\n'
      + 'This is not the global “Run scheduled drip now”. Continue?'
    );
  }

  const invoiceLine = hasLinked
    ? '• Use the already linked Bexio invoice (no duplicate create)\n'
    : `• Create & issue a Bexio invoice (${fee} ${currency}) due on ${feeDate}\n`;
  const emailLine = early
    ? `• Do NOT email the customer yet — invoice PDF email sends on ${feeDate} to: ${recipients}\n`
    : `• Email the invoice PDF now to: ${recipients}\n`;
  return (
    `Launch invoice & reminders for ${domain}?\n\n`
    + 'This will:\n'
    + invoiceLine
    + emailLine
    + '• Download the invoice PDF in your browser\n'
    + '• Link the invoice to this line and enrol its reminder schedule (no backfill)\n\n'
    + 'This is not the global “Run scheduled drip now”. Continue?'
  );
}

/** True when the maintenance line has explicit recipient email(s) for Launch. */
export function hasLaunchRecipients(item) {
  return String(item?.responsible_email || '').trim().length > 0;
}

/**
 * Existing-Issued Launch skip marker — durable delivery-complete claim that is
 * not a real customer email. Activity must never render these as Live/sent.
 */
export function isExistingInvoiceReuseSkip(row) {
  const step = String(row?.step_key || '');
  if (step !== 'invoice_delivery' && step !== 'launch:invoice') return false;
  const phase = String(row?.phase || '');
  const error = String(row?.error || '');
  return (
    phase === 'existing_invoice_reused'
    || error === 'existing_issued_invoice_skipped'
    || error === 'existing_invoice_reused'
  );
}
